/**
 * Native Shoes — Cart add safety net
 * ────────────────────────────────────────────────────────────────────────────
 * Author:   Anton Siedlecki
 * Version:  1.0.0
 * Date:     2026-10-07
 *
 * WHY
 * A real session on the dev store hit "The product '…' is already sold out."
 * on an Add to bag for a size that the page, the Admin and the Storefront API
 * all showed as in stock. Incognito (a fresh cart) worked, and emptying that
 * same cart (same token) made the add work too, so the refusal was tied to
 * something in the cart's contents/state, not to stock, market, login or
 * cookies. The cause was never reproduced outside that browser window.
 *
 * WHAT THIS DOES
 * assets/product-form.js asks us whether to try again when an add comes back
 * 422 "sold out". We:
 *   1. snapshot the bag (lines + any discount strings on the cart),
 *   2. empty it,
 *   3. repeat the SAME add request (same form data),
 *   4. if that worked: put the old lines and discount strings back, then fetch
 *      fresh drawer sections so the drawer shows the real bag, and hand
 *      product-form a normal success response,
 *   5. if it still failed: put the old bag back exactly and return null, so
 *      product-form shows the original error.
 * It runs at most once per add. A genuinely sold-out size still fails on the
 * repeat and shows the normal message. An empty bag is never "healed" (nothing
 * in the cart could be the cause).
 *
 * Window.NativeShops.cartHeal = { shouldTry(response), rebuild(formData) }
 * Debug: every step logs with the prefix "[native-cart-heal]".
 */
(function () {
  window.NativeShops = window.NativeShops || {};
  if (window.NativeShops.cartHeal) return;

  var busy = false;

  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function log() {
    try { console.info.apply(console, ['[native-cart-heal]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function request(path, init) {
    return fetch(root() + path, init).then(function (res) {
      return res.text().then(function (text) {
        var body;
        try { body = JSON.parse(text); } catch (e) { body = text; }
        return { ok: res.ok, status: res.status, body: body };
      });
    });
  }

  function postJson(path, payload) {
    return request(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: payload ? JSON.stringify(payload) : undefined
    });
  }

  function shouldTry(response) {
    if (busy || !response || response.status !== 422) return false;
    var text = String(response.description || response.message || '');
    return /sold out/i.test(text);
  }

  function snapshot() {
    return request('cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) {
      var cart = (r.ok && r.body) || {};
      var lines = (cart.items || []).map(function (i) {
        var line = { id: i.variant_id, quantity: i.quantity };
        if (i.properties && Object.keys(i.properties).length) line.properties = i.properties;
        if (i.selling_plan_allocation && i.selling_plan_allocation.selling_plan) {
          line.selling_plan = i.selling_plan_allocation.selling_plan.id;
        }
        return line;
      });
      var strings = (cart.discount_codes || []).map(function (d) { return d.code; });
      return { lines: lines, strings: strings, count: cart.item_count || 0 };
    });
  }

  // Put the bag back. Batch first (one request); if any line is refused, fall back to
  // one by one so a single unavailable line cannot drop the rest. Returns how many lines failed.
  function restore(snap) {
    if (!snap.lines.length) return Promise.resolve(0);
    return postJson('cart/add.js', { items: snap.lines }).then(function (r) {
      if (r.ok) return 0;
      var failed = 0;
      return snap.lines.reduce(function (chain, line) {
        return chain.then(function () {
          return postJson('cart/add.js', { items: [line] }).then(function (one) { if (!one.ok) failed++; });
        });
      }, Promise.resolve()).then(function () { return failed; });
    });
  }

  function restoreStrings(snap) {
    if (!snap.strings.length) return Promise.resolve();
    return postJson('cart/update.js', { discount: snap.strings.join(',') });
  }

  function freshSections(formData) {
    var names = String(formData.get('sections') || 'cart-drawer,cart-icon-bubble');
    return request('?sections=' + encodeURIComponent(names), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok && r.body && typeof r.body === 'object' ? r.body : null; });
  }

  function rebuild(formData) {
    if (busy) return Promise.resolve(null);
    busy = true;
    var snap;
    var done = function (value) { busy = false; return value; };

    return snapshot().then(function (s) {
      snap = s;
      if (!snap.lines.length) { log('bag is empty, nothing to rebuild'); return done(null); }
      log('add refused as sold out; rebuilding the bag once (' + snap.lines.length + ' line(s))');

      return postJson('cart/clear.js').then(function () {
        return request('cart/add', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
      }).then(function (added) {
        if (!added.ok || !added.body || added.body.status) {
          log('repeat add still refused; restoring the original bag');
          return restore(snap).then(function () { return restoreStrings(snap); }).then(function () { return done(null); });
        }
        return restore(snap).then(function (failed) {
          if (failed) log(failed + ' earlier line(s) could not be put back (the drawer shows the real bag)');
          return restoreStrings(snap);
        }).then(function () {
          return freshSections(formData);
        }).then(function (sections) {
          if (!sections) return done(null);
          log('rebuilt the bag; add succeeded');
          return done(Object.assign({}, added.body, { sections: sections }));
        });
      });
    }).catch(function (e) {
      log('rebuild failed, leaving the normal error in place', e && e.message);
      return done(null);
    });
  }

  window.NativeShops.cartHeal = { shouldTry: shouldTry, rebuild: rebuild };
})();
